import { useCallback, useState } from "react";
import {
  listCategoryChangeRequests,
  resolveCategoryChangeRequest,
} from "../../auth/shared/services/authService";
import {
  CategoryChangeRequest,
  CategoryChangeRequestStatus,
} from "../../auth/shared/types/auth";
import { siteTexts } from "../../../shared/i18n/siteTexts";

type UseCategoryChangeRequestsAdminParams = {
  isAdmin: boolean;
  loadUsers: () => Promise<void>;
  showErrorToast: (message: string) => void;
  showSuccessToast: (message: string) => void;
  token?: string | null;
  usersCount: number;
};

export function useCategoryChangeRequestsAdmin({
  isAdmin,
  loadUsers,
  showErrorToast,
  showSuccessToast,
  token,
  usersCount,
}: UseCategoryChangeRequestsAdminParams) {
  const t = siteTexts.admin;
  const [categoryChangeRequests, setCategoryChangeRequests] = useState<
    CategoryChangeRequest[]
  >([]);
  const [categoryChangeRequestsLoading, setCategoryChangeRequestsLoading] =
    useState(false);
  const [categoryChangeRequestsStatus, setCategoryChangeRequestsStatus] =
    useState<CategoryChangeRequestStatus | "all">("pending");
  const [categoryChangeResolveLoadingId, setCategoryChangeResolveLoadingId] =
    useState<number | null>(null);
  const [categoryChangeAdminNotes, setCategoryChangeAdminNotes] = useState<
    Record<number, string>
  >({});

  const loadCategoryChangeRequests = useCallback(async () => {
    if (!token || !isAdmin) return;
    setCategoryChangeRequestsLoading(true);
    try {
      const rows = await listCategoryChangeRequests(token, {
        status: categoryChangeRequestsStatus,
      });
      setCategoryChangeRequests(rows);
      setCategoryChangeAdminNotes((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          if (next[row.id] === undefined) {
            next[row.id] = row.adminNote || "";
          }
        });
        return next;
      });
    } catch (_err) {
      setCategoryChangeRequests([]);
    } finally {
      setCategoryChangeRequestsLoading(false);
    }
  }, [token, isAdmin, categoryChangeRequestsStatus]);

  const onResolveCategoryChangeRequest = useCallback(
    async (requestId: number, action: "approve" | "reject") => {
      if (!token || !isAdmin) return;
      try {
        setCategoryChangeResolveLoadingId(requestId);
        await resolveCategoryChangeRequest(token, requestId, {
          action,
          adminNote: categoryChangeAdminNotes[requestId] || "",
        });
        showSuccessToast(
          action === "approve"
            ? t.categoryRequestsTab.toasts.approved
            : "Solicitação recusada.",
        );
        await loadCategoryChangeRequests();
        if (usersCount > 0) {
          await loadUsers();
        }
      } catch (_err) {
        showErrorToast(t.categoryRequestsTab.toasts.error);
      } finally {
        setCategoryChangeResolveLoadingId(null);
      }
    },
    [
      token,
      isAdmin,
      categoryChangeAdminNotes,
      showSuccessToast,
      loadCategoryChangeRequests,
      usersCount,
      loadUsers,
      showErrorToast,
      t.categoryRequestsTab.toasts.approved,
      t.categoryRequestsTab.toasts.error,
    ],
  );

  return {
    categoryChangeAdminNotes,
    categoryChangeRequests,
    categoryChangeRequestsLoading,
    categoryChangeRequestsStatus,
    categoryChangeResolveLoadingId,
    loadCategoryChangeRequests,
    onResolveCategoryChangeRequest,
    setCategoryChangeAdminNotes,
    setCategoryChangeRequestsStatus,
  };
}
